import { ISpecificUsers, PostVisibilityEnum } from 'redux-store/api/activity-feed';
import {
  ISourcedProjectAudiences,
  SourceEnum,
  TConvertedAudience,
  TransformedAudience,
} from './types';
import { getCapitalizedString } from 'utils/helpers';

const transformByRoleGroup = (group: ISpecificUsers['byRole'][number]) => ({
  ...group,
  groupName: getCapitalizedString(group?.groupName),
  source: SourceEnum.BY_ROLE,
});

const transformCustomGroup = (group: ISpecificUsers['customGroups'][number]) => ({
  ...group,
  source: SourceEnum.CUSTOM_GROUPS,
});

export const convertProjectAudience = (
  projectAudience: Omit<ISpecificUsers, 'customUsers'>
): ISourcedProjectAudiences => {
  const { byRole = [], customGroups = [] } = projectAudience || {};

  return {
    byRole: byRole.map(transformByRoleGroup),
    customGroups: customGroups.map(transformCustomGroup),
  };
};

export const convertPostAudience = (
  postVisibility: PostVisibilityEnum,
  postAudience: ISpecificUsers
): TConvertedAudience | null => {
  if (!postAudience) return null;

  return { postVisibility, ...postAudience };
};

export const transformAudience = (
  audience: TConvertedAudience,
  showAudience: boolean
): TransformedAudience => {
  if (!audience || !showAudience) {
    return {
      visibility: [-1],
      postVisibility: PostVisibilityEnum.EVERYONE,
    };
  }

  const { byRole = [], customGroups = [], customUsers = [], postVisibility } = audience;

  if (postVisibility === PostVisibilityEnum.EVERYONE) {
    return {
      visibility: [-1],
      postVisibility: PostVisibilityEnum.EVERYONE,
    };
  }

  if (postVisibility === PostVisibilityEnum.JUST_ME) {
    return {
      visibility: [-2],
      postVisibility: PostVisibilityEnum.JUST_ME,
    };
  }

  const profileIds = new Set<number>();
  byRole.forEach((role) => role.profiles?.forEach((p) => profileIds.add(p.profileId)));
  customGroups.forEach((group) => group.profiles?.forEach((p) => profileIds.add(p.profileId)));
  customUsers.forEach((user) => profileIds.add(user.profileId));

  return {
    visibility: Array.from(profileIds),
    postVisibility: PostVisibilityEnum.SPECIFIC_MEMBERS,
    audienceRoles: byRole.map(({ id }) => id),
    audienceGroups: customGroups.map(({ id }) => id),
    visibleProfileIds: customUsers.map(({ profileId }) => profileId),
  };
};
